import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// CSS keyframes injected once
const INTRO_CSS = `
@keyframes intro-zoom-in { 0% { transform: scale(0.2); opacity: 0; } 60% { transform: scale(1.08); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
@keyframes intro-fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes intro-drop-in { 0% { transform: translateY(-120vh) rotate(-12deg); opacity: 0; } 70% { transform: translateY(20px) rotate(2deg); opacity: 1; } 100% { transform: translateY(0) rotate(0); opacity: 1; } }
@keyframes intro-pop-in { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.18); opacity: 1; } 80% { transform: scale(0.92); } 100% { transform: scale(1); opacity: 1; } }

@keyframes intro-explode-img { 0% { transform: scale(1); filter: brightness(1) blur(0); opacity: 1; } 35% { transform: scale(1.5); filter: brightness(2.4) blur(2px); opacity: 1; } 100% { transform: scale(3.4); filter: brightness(3.5) blur(18px); opacity: 0; } }
@keyframes intro-explode-flash { 0% { transform: scale(0.1); opacity: 0; } 25% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(3.4); opacity: 0; } }
@keyframes intro-shard { 0% { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 1; } 100% { transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0); opacity: 0; } }

@keyframes intro-zoom-out { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(0); opacity: 0; } }
@keyframes intro-fade-out { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes intro-drop-out { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(120vh) rotate(12deg); opacity: 0; } }

@keyframes intro-overlay-fade { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes intro-overlay-fade-in { 0% { opacity: 0; } 100% { opacity: 1; } }
@keyframes intro-btn-pulse { 0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.7); } 50% { transform: scale(1.08); box-shadow: 0 0 0 14px rgba(251, 191, 36, 0); } }

/* Extra entry effects */
@keyframes intro-slide-up { 0% { transform: translateY(60vh); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
@keyframes intro-slide-left { 0% { transform: translateX(-60vw); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
@keyframes intro-slide-right { 0% { transform: translateX(60vw); opacity: 0; } 100% { transform: translateX(0); opacity: 1; } }
@keyframes intro-bounce-in { 0% { opacity: 0; transform: scale(0.3); } 55% { opacity: 1; transform: scale(1.18); } 75% { transform: scale(0.92); } 100% { transform: scale(1); } }
@keyframes intro-spin-in { 0% { transform: rotate(-540deg) scale(0); opacity: 0; } 70% { opacity: 1; } 100% { transform: rotate(0) scale(1); opacity: 1; } }
@keyframes intro-flip-in { 0% { transform: rotateY(90deg) scale(0.6); opacity: 0; } 100% { transform: rotateY(0) scale(1); opacity: 1; } }
@keyframes intro-blur-in { 0% { filter: blur(28px); opacity: 0; transform: scale(0.92); } 100% { filter: blur(0); opacity: 1; transform: scale(1); } }
@keyframes intro-elastic-in { 0% { transform: scale(0); opacity: 0; } 40% { transform: scale(1.4); opacity: 1; } 60% { transform: scale(0.85); } 80% { transform: scale(1.08); } 100% { transform: scale(1); } }
@keyframes intro-swing-in { 0% { transform: rotate(-25deg) translateY(-40vh); opacity: 0; } 60% { transform: rotate(8deg) translateY(0); opacity: 1; } 80% { transform: rotate(-4deg); } 100% { transform: rotate(0); } }

/* Extra exit effects */
@keyframes intro-slide-up-out { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-110vh); opacity: 0; } }
@keyframes intro-slide-left-out { 0% { transform: translateX(0); opacity: 1; } 100% { transform: translateX(-110vw); opacity: 0; } }
@keyframes intro-slide-right-out { 0% { transform: translateX(0); opacity: 1; } 100% { transform: translateX(110vw); opacity: 0; } }
@keyframes intro-spin-out { 0% { transform: rotate(0) scale(1); opacity: 1; } 100% { transform: rotate(540deg) scale(0); opacity: 0; } }
@keyframes intro-flip-out { 0% { transform: rotateY(0) scale(1); opacity: 1; } 100% { transform: rotateY(90deg) scale(0.5); opacity: 0; } }
@keyframes intro-blur-out { 0% { filter: blur(0); opacity: 1; transform: scale(1); } 100% { filter: blur(28px); opacity: 0; transform: scale(1.1); } }
@keyframes intro-shrink-out { 0% { transform: scale(1) rotate(0); opacity: 1; } 100% { transform: scale(0) rotate(180deg); opacity: 0; } }
@keyframes intro-pixelate-out { 0% { filter: contrast(1) saturate(1); opacity: 1; } 100% { filter: contrast(4) saturate(0) blur(6px); opacity: 0; } }

/* Backdraft / retroexplosão — first implodes inward (with air-being-sucked
   hue-rotate + contrast distortion), then violently bursts outward. */
@keyframes intro-backdraft-img {
  0%   { transform: scale(1);   filter: brightness(1)   contrast(1)   hue-rotate(0deg)    blur(0); opacity: 1; }
  18%  { transform: scale(0.62); filter: brightness(0.75) contrast(1.35) hue-rotate(-22deg)  blur(0.8px); opacity: 1; }
  30%  { transform: scale(0.35); filter: brightness(0.55) contrast(1.85) hue-rotate(-48deg)  blur(1.4px); opacity: 1; }
  42%  { transform: scale(0.22); filter: brightness(1.4)  contrast(2.4)  hue-rotate(-90deg)  blur(0.6px); opacity: 1; }
  55%  { transform: scale(0.5);  filter: brightness(3.2)  contrast(1.6)  hue-rotate(-30deg)  blur(0); opacity: 1; }
  72%  { transform: scale(1.6);  filter: brightness(3.8)  contrast(1.2)  hue-rotate(0deg)    blur(6px); opacity: 0.95; }
  100% { transform: scale(4.2);  filter: brightness(4)    contrast(1)    hue-rotate(0deg)    blur(22px); opacity: 0; }
}
@keyframes intro-backdraft-flash {
  0%   { transform: scale(0); opacity: 0; }
  40%  { transform: scale(0.15); opacity: 0; }
  55%  { transform: scale(0.6); opacity: 1; }
  75%  { transform: scale(2.2); opacity: 0.85; }
  100% { transform: scale(4.5); opacity: 0; }
}
@keyframes intro-backdraft-shard {
  0%   { transform: translate(0, 0) rotate(0deg) scale(1); opacity: 0; }
  40%  { transform: translate(0, 0) rotate(0deg) scale(0.5); opacity: 0; }
  55%  { transform: translate(0, 0) rotate(0deg) scale(1.2); opacity: 1; }
  100% { transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(0); opacity: 0; }
}
`;

const ENTER_ANIM = {
  zoom_in: "intro-zoom-in 0.85s cubic-bezier(.2,.7,.3,1) both",
  fade: "intro-fade-in 0.7s ease both",
  drop: "intro-drop-in 0.9s cubic-bezier(.3,1.4,.4,1) both",
  pop: "intro-pop-in 0.7s cubic-bezier(.3,1.6,.4,1) both",
  slide_up: "intro-slide-up 0.8s cubic-bezier(.2,.7,.3,1) both",
  slide_left: "intro-slide-left 0.8s cubic-bezier(.2,.7,.3,1) both",
  slide_right: "intro-slide-right 0.8s cubic-bezier(.2,.7,.3,1) both",
  bounce_in: "intro-bounce-in 1s cubic-bezier(.34,1.7,.64,1) both",
  spin_in: "intro-spin-in 1s cubic-bezier(.34,1.4,.64,1) both",
  flip_in: "intro-flip-in 0.85s cubic-bezier(.34,1.4,.64,1) both",
  blur_in: "intro-blur-in 0.9s ease both",
  elastic_in: "intro-elastic-in 1.1s cubic-bezier(.34,1.7,.5,1) both",
  swing_in: "intro-swing-in 1.05s cubic-bezier(.34,1.4,.5,1) both",
};
const EXIT_ANIM = {
  explode: "intro-explode-img 0.6s ease-in both",
  zoom_out: "intro-zoom-out 0.5s ease-in both",
  fade: "intro-fade-out 0.5s ease both",
  drop: "intro-drop-out 0.6s cubic-bezier(.5,0,.7,.4) both",
  morph: "intro-morph-out 1.3s cubic-bezier(.55,.05,.45,1) both",
  slide_up_out: "intro-slide-up-out 0.6s cubic-bezier(.5,0,.7,.4) both",
  slide_left_out: "intro-slide-left-out 0.6s cubic-bezier(.5,0,.7,.4) both",
  slide_right_out: "intro-slide-right-out 0.6s cubic-bezier(.5,0,.7,.4) both",
  spin_out: "intro-spin-out 0.7s cubic-bezier(.5,0,.7,.4) both",
  flip_out: "intro-flip-out 0.6s ease-in both",
  blur_out: "intro-blur-out 0.7s ease both",
  shrink_out: "intro-shrink-out 0.7s cubic-bezier(.5,0,.7,.4) both",
  pixelate_out: "intro-pixelate-out 0.7s ease both",
  backdraft: "intro-backdraft-img 1.05s cubic-bezier(.5,0,.7,.4) both",
};

const SESSION_KEY = "supertmatik_intro_seen";
const FIRST_VISIT_KEY = "supertmatik_intro_visited"; // localStorage — persistente entre sessões
// Persistent cache for intro settings so the splash can render in the SAME
// frame as the homepage (no fetch delay on first paint). TTL keeps it in
// sync with admin edits without a hard refresh in-between.
const INTRO_CACHE_KEY = "supertmatik_intro_settings_v1";
const INTRO_CACHE_TS_KEY = "supertmatik_intro_settings_v1.ts";
const INTRO_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function readIntroCache() {
  try {
    const ts = Number(localStorage.getItem(INTRO_CACHE_TS_KEY) || 0);
    if (!ts || Date.now() - ts > INTRO_CACHE_TTL_MS) return null;
    const raw = localStorage.getItem(INTRO_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeIntroCache(data) {
  try {
    localStorage.setItem(INTRO_CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(INTRO_CACHE_TS_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

export default function IntroSplash({ forcePreview = false }) {
  const { lang, setLang } = useI18n();
  // Initialise settings SYNCHRONOUSLY from the localStorage cache so the
  // intro overlay renders in the SAME frame as the homepage — no fetch delay,
  // no flash of unstyled homepage-then-splash. If no cache exists, we fall
  // back to `null` and the useEffect below fetches from the API.
  const [settings, setSettings] = useState(() => readIntroCache());
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [overlayFading, setOverlayFading] = useState(false);
  const lastLangRef = useRef(null);
  const timerRef = useRef(null);
  const imgRef = useRef(null);
  const [morphVars, setMorphVars] = useState(null);

  // Inject CSS once
  useEffect(() => {
    if (document.getElementById("intro-splash-css")) return;
    const s = document.createElement("style");
    s.id = "intro-splash-css";
    s.textContent = INTRO_CSS;
    document.head.appendChild(s);
  }, []);

  // Load settings on mount (background revalidation — updates cache too).
  useEffect(() => {
    api.get("/intro/settings").then((r) => {
      if (r?.data) {
        setSettings(r.data);
        writeIntroCache(r.data);
      }
    }).catch(() => {});
  }, []);

  // Decide when to show — on mount + every time language changes.
  useEffect(() => {
    if (!settings || !settings.enabled) return;
    const prev = lastLangRef.current;
    lastLangRef.current = lang;

    if (prev === null) {
      // first mount: respect frequency
      if (settings.frequency === "session") {
        const seen = sessionStorage.getItem(SESSION_KEY);
        if (seen) return;
      } else if (settings.frequency === "first_visit") {
        // Only show on the very first visit ever (persists across sessions).
        const visited = localStorage.getItem(FIRST_VISIT_KEY);
        if (visited) return;
      }
    }
    // Language switch ALWAYS reshows (independent of frequency)
    setVisible(true);
    setExiting(false);
    setOverlayFading(false);
  }, [settings, lang]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!visible || !settings) return;
    if (settings.wait_for_click) return;
    // When the 3 language buttons are visible, the splash MUST wait for the
    // visitor to explicitly choose a language before proceeding. Auto-dismiss
    // is bypassed entirely so the choice is deliberate.
    if (settings.show_language_buttons !== false) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => triggerDismiss(), Math.max(0.5, settings.duration_sec || 3) * 1000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [visible, settings]);

  const triggerDismiss = () => {
    if (exiting) return;
    const isMorphFx = settings.exit_effect === "morph";
    // For morph: measure the actual home_title_image (or home-title h1) rect and
    // compute the translate/scale that takes the splash image precisely to that
    // position + shape. This makes the suction visually "land" on the logo.
    if (isMorphFx && imgRef.current) {
      const splashRect = imgRef.current.getBoundingClientRect();
      const target =
        document.querySelector("[data-testid='home-title-image']") ||
        document.querySelector("[data-testid='home-title']");
      if (splashRect.width > 0 && target) {
        const targetRect = target.getBoundingClientRect();
        const sx = Math.max(0.05, targetRect.width / splashRect.width);
        const sy = Math.max(0.05, targetRect.height / splashRect.height);
        const splashCenterX = splashRect.left + splashRect.width / 2;
        const splashCenterY = splashRect.top + splashRect.height / 2;
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;
        setMorphVars({
          mx: `${Math.round(targetCenterX - splashCenterX)}px`,
          my: `${Math.round(targetCenterY - splashCenterY)}px`,
          sx,
          sy,
        });
      }
    }
    setExiting(true);
    sessionStorage.setItem(SESSION_KEY, "1");
    // Persistir "já visitou" no localStorage para o modo "first_visit".
    // Guardamos sempre — inofensivo para os outros modos.
    try { localStorage.setItem(FIRST_VISIT_KEY, "1"); } catch (_e) { /* storage disabled */ }
    const isBackdraftFx = settings.exit_effect === "backdraft" || settings.title_exit_effect === "backdraft";
    // For morph: overlay fades IMMEDIATELY so the user sees the home page behind
    // and can clearly see the splash image being sucked towards the title area.
    // For backdraft: overlay fades only after the implosion (delayed) for darkness.
    // For other effects: keep the original behaviour (overlay fades at 450ms).
    const overlayFadeDelay = isMorphFx ? 0 : isBackdraftFx ? 700 : 450;
    setTimeout(() => setOverlayFading(true), overlayFadeDelay);
    const totalMs = isMorphFx ? 1300 : isBackdraftFx ? 1200 : 1100;
    // Notify the Home page so it can pulse the title logo right when the morph lands
    if (isMorphFx) {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("supertmatik:morph-arrived"));
      }, 1100);
    }
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      setOverlayFading(false);
      // Always notify listeners that the splash has FULLY finished — Home/main pages
      // use this to delay rendering of their content until the intro is done.
      window.dispatchEvent(new CustomEvent("supertmatik:intro-finished"));
    }, totalMs);
  };

  // Don't show intro on /admin/* routes (unless forcePreview)
  if (!forcePreview && typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    return null;
  }
  if (!visible || !settings) return null;

  const imgKey = `image_${lang}`;
  const img =
    settings[imgKey] || settings.image_pt || settings.image_en || settings.image_es;
  const titleTextKey = `title_text_${lang}`;
  const titleText =
    settings[titleTextKey] || settings.title_text_pt || settings.title_text_en || settings.title_text_es || "Intro";
  const btnKey = `button_text_${lang}`;
  const btnText = settings[btnKey] || settings.button_text_pt || "Entrar";
  const dim = Math.max(0, Math.min(100, settings.dim_pct ?? 70)) / 100;
  // Convert configurable hex color + dim opacity → rgba string. Hex may be
  // in `#RGB` or `#RRGGBB` form; fall back to black on malformed input.
  const hexToRgb = (hex) => {
    if (!hex || typeof hex !== "string") return { r: 0, g: 0, b: 0 };
    let h = hex.trim().replace(/^#/, "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (h.length !== 6) return { r: 0, g: 0, b: 0 };
    const n = parseInt(h, 16);
    if (Number.isNaN(n)) return { r: 0, g: 0, b: 0 };
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };
  const dimRgb = hexToRgb(settings.dim_color || "#000000");
  const dimBg = `rgba(${dimRgb.r},${dimRgb.g},${dimRgb.b},${dim})`;
  const enterAnim = ENTER_ANIM[settings.enter_effect] || ENTER_ANIM.zoom_in;
  const exitAnim = EXIT_ANIM[settings.exit_effect] || EXIT_ANIM.explode;
  // Title-specific overrides (separate effect + delay).
  const titleEnterAnim = ENTER_ANIM[settings.title_enter_effect] || enterAnim;
  const titleExitAnim = EXIT_ANIM[settings.title_exit_effect] || exitAnim;
  const titleEnterDelayMs = Math.max(0, Math.min(3000, parseInt(settings.title_enter_delay_ms ?? 0, 10) || 0));
  const titleExitDelayMs = Math.max(0, Math.min(3000, parseInt(settings.title_exit_delay_ms ?? 0, 10) || 0));
  const titleAnim = exiting ? titleExitAnim : titleEnterAnim;
  const titleDelay = `${(exiting ? titleExitDelayMs : titleEnterDelayMs) / 1000}s`;
  const isExplode = exiting && (settings.title_exit_effect === "explode" || settings.exit_effect === "explode");
  const isBackdraft = exiting && (settings.title_exit_effect === "backdraft" || settings.exit_effect === "backdraft");
  const isMorph = exiting && (settings.title_exit_effect === "morph" || settings.exit_effect === "morph");
  // Sparkle trail directions for morph (12 sparkles moving up to the title position)
  const sparkles = Array.from({ length: 14 }, (_, i) => {
    const spread = 240;
    const tx = (Math.random() - 0.5) * spread;
    const ty = -300 - Math.random() * 200;
    const colors = ["#fbbf24", "#f43f5e", "#3b82f6", "#22c55e", "#a855f7", "#fff", "#06b6d4"];
    return { tx, ty, color: colors[i % colors.length], i, delay: Math.random() * 0.25 };
  });

  // Shards configuration for explosion (16 directions)
  const shards = Array.from({ length: 16 }, (_, i) => {
    const angle = (i / 16) * Math.PI * 2;
    const dist = 380 + Math.random() * 220;
    const tx = Math.cos(angle) * dist;
    const ty = Math.sin(angle) * dist;
    const rot = (Math.random() * 720 - 360) + "deg";
    const colors = ["#fbbf24", "#f43f5e", "#3b82f6", "#22c55e", "#a855f7", "#fff", "#fb923c", "#06b6d4"];
    const color = colors[i % colors.length];
    return { tx, ty, rot, color, i };
  });

  return (
    <div
      data-testid="intro-splash"
      onClick={() => {
        // If the 3 language buttons are visible, the visitor must click a
        // language explicitly — background/backdrop clicks are ignored so the
        // choice is unambiguous.
        if (settings.show_language_buttons !== false) return;
        triggerDismiss();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
        cursor: settings.show_language_buttons !== false ? "default" : "pointer",
      }}
    >
      {/* Dark overlay */}
      <div
        data-testid="intro-overlay"
        style={{
          position: "absolute",
          inset: 0,
          background: dimBg,
          animation: overlayFading
            ? (isMorph ? "intro-overlay-fade 1.0s ease forwards" : "intro-overlay-fade 0.5s ease forwards")
            : "intro-overlay-fade-in 0.3s ease both",
        }}
      />

      {/* Explosion flash */}
      {isExplode && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,250,225,1) 0%, rgba(251,191,36,0.95) 30%, rgba(244,63,94,0.6) 60%, transparent 80%)",
            filter: "blur(2px)",
            animation: "intro-explode-flash 0.6s ease-out forwards",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Backdraft flash — delayed implosion then white-hot burst */}
      {isBackdraft && (
        <div
          aria-hidden
          style={{
            position: "absolute",
            width: "260px",
            height: "260px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,240,150,0.95) 25%, rgba(251,146,60,0.7) 55%, rgba(244,63,94,0.4) 75%, transparent 90%)",
            filter: "blur(3px)",
            animation: "intro-backdraft-flash 1.05s cubic-bezier(.5,0,.3,1) forwards",
            pointerEvents: "none",
          }}
        />
      )}

      {/* Shards (explode + backdraft share the same shard system) */}
      {(isExplode || isBackdraft) && (
        <div aria-hidden style={{ position: "absolute", width: 0, height: 0 }}>
          {shards.map((s) => (
            <div
              key={s.i}
              style={{
                position: "absolute",
                width: 14,
                height: 14,
                background: s.color,
                borderRadius: 3,
                boxShadow: `0 0 12px ${s.color}`,
                "--tx": `${s.tx}px`,
                "--ty": `${s.ty}px`,
                "--rot": s.rot,
                animation: isBackdraft
                  ? `intro-backdraft-shard ${1.0 + Math.random() * 0.2}s cubic-bezier(.5,0,.3,1) forwards`
                  : `intro-shard ${0.65 + Math.random() * 0.25}s cubic-bezier(.2,.7,.5,1) forwards`,
              }}
            />
          ))}
        </div>
      )}

      {/* Morph sparkle trail */}
      {isMorph && (
        <div aria-hidden style={{ position: "absolute", width: 0, height: 0 }}>
          {sparkles.map((s) => (
            <div
              key={s.i}
              style={{
                position: "absolute",
                width: 10,
                height: 10,
                background: s.color,
                borderRadius: "50%",
                boxShadow: `0 0 10px ${s.color}`,
                "--tx": `${s.tx}px`,
                "--ty": `${s.ty}px`,
                animation: `intro-suck-trail ${0.85 + Math.random() * 0.25}s ${s.delay}s cubic-bezier(.4,0,.2,1) forwards`,
              }}
            />
          ))}
        </div>
      )}

      {/* The image + button */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 18,
          maxWidth: "90vw",
        }}
      >
        {img ? (
          <img
            data-testid="intro-image"
            ref={imgRef}
            src={img}
            alt="intro"
            style={{
              maxWidth: "min(86vw, 640px)",
              maxHeight: "65vh",
              objectFit: "contain",
              animation: titleAnim,
              animationDelay: titleDelay,
              filter: !exiting ? "drop-shadow(0 8px 40px rgba(0,0,0,0.6))" : undefined,
              transformOrigin: "center center",
              ...(morphVars
                ? { "--mx": morphVars.mx, "--my": morphVars.my, "--sx": morphVars.sx, "--sy": morphVars.sy }
                : { "--mx": "0px", "--my": "-40vh", "--sx": 0.1, "--sy": 0.1 }),
            }}
          />
        ) : (
          <div
            data-testid="intro-fallback-text"
            style={{
              padding: "32px 56px",
              background: "#1f2937",
              color: "#fff",
              borderRadius: 12,
              fontFamily: "Fredoka, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(28px, 6vw, 64px)",
              letterSpacing: "0.04em",
              textAlign: "center",
              boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
              animation: titleAnim,
              animationDelay: titleDelay,
              transformOrigin: "center center",
            }}
          >
            {titleText}
          </div>
        )}
        {/* Language buttons — appear immediately after the image and let the
            visitor jump straight into the site in their preferred language.
            Clicking one:
              1. Persists the language via i18n setLang (writes localStorage)
              2. Triggers the same dismissal animation as the "Entrar" button */}
        {settings.show_language_buttons !== false && !exiting && (
          <div
            data-testid="intro-lang-buttons"
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              justifyContent: "center",
              animation: "intro-fade-in 0.6s ease 0.15s both",
            }}
          >
            {[
              { code: "pt", flag: "🇵🇹", label: "PORTUGUÊS" },
              { code: "en", flag: "🇬🇧", label: "ENGLISH" },
              { code: "es", flag: "🇪🇸", label: "ESPAÑOL" },
            ].map(({ code, flag, label }) => (
              <button
                key={code}
                data-testid={`intro-lang-btn-${code}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (setLang) setLang(code);
                  try { localStorage.setItem("lang", code); } catch (err) { /* noop */ }
                  triggerDismiss();
                }}
                style={{
                  padding: "10px 22px",
                  fontSize: 15,
                  fontWeight: 800,
                  fontFamily: "Fredoka, sans-serif",
                  color: lang === code ? "#0F172A" : "#FFFFFF",
                  background: lang === code ? (settings.button_color || "#CFE40F") : "rgba(255,255,255,0.12)",
                  border: `3px solid ${lang === code ? "#FFFFFF" : "rgba(255,255,255,0.4)"}`,
                  borderRadius: 999,
                  cursor: "pointer",
                  letterSpacing: "0.05em",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  backdropFilter: "blur(6px)",
                  boxShadow: lang === code ? "0 6px 20px rgba(207,228,15,0.35)" : "0 4px 12px rgba(0,0,0,0.35)",
                  transition: "transform 160ms ease, background 160ms ease, color 160ms ease, border-color 160ms ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px) scale(1.04)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
              >
                <span aria-hidden style={{ fontSize: 20, lineHeight: 1 }}>{flag}</span>
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}
        {settings.show_button && !exiting && (
          <button
            data-testid="intro-enter-btn"
            onClick={(e) => { e.stopPropagation(); triggerDismiss(); }}
            style={{
              padding: "12px 36px",
              fontSize: 18,
              fontWeight: 800,
              fontFamily: "Fredoka, sans-serif",
              color: "#1f2937",
              background: settings.button_color || "#CFE40F",
              border: "3px solid #fff",
              borderRadius: 999,
              cursor: "pointer",
              animation: "intro-btn-pulse 1.6s infinite",
              letterSpacing: "0.05em",
            }}
          >
            {btnText}
          </button>
        )}
      </div>
    </div>
  );
}
